import gql from 'graphql-tag';
import { ChannelOwner, ChannelSubscription, ChannelTypes, IdAndName } from '../shared/types';

export const CHANNEL_FIELDS_FRAGMENT = `
	_id
	name
	ownerType
	ownerId
	owner {
		_id
		name
	}
	price
	description
	url
	profileImage
	coverImage
	isCurrentUserSubscribed
	defaultTrialDurationDays
	type
	hideDisclaimer
`;

export const GET_CHANNELS = gql`
	query channels {
		channels {
			${CHANNEL_FIELDS_FRAGMENT}
		}
	}`;

export const GET_PREMIUM_CHANNELS = gql`
	query premiumChannels {
		premiumChannels (excludeSubscriptions: true) {
			${CHANNEL_FIELDS_FRAGMENT}
		}
	}`;

export const GET_FREE_CHANNELS = gql`
	query freeChannels {
		freeChannels (excludeSubscriptions: true) {
			${CHANNEL_FIELDS_FRAGMENT}
		}
	}`;

export const GET_CHANNELS_INFO = gql`
	query channels {
		channels {
			_id
			name
		}
	}`;

export const GET_CHANNELS_PUBLISHERS_DASHBOARD_SHORT = gql`
	query channels {
		channels {
			_id
			name
			ownerType
			ownerId
			owner {
				_id
				name
			}
		}
	}`;

export const GET_CHANNELS_ADD_EDIT_ARTICLE = gql`
query channels {
	channels {
		ownerType
		${CHANNEL_FIELDS_FRAGMENT}
		adminUsers {
			_id
		}
		channelActiveUsersCount
		articleCount
		createdAt
		createdBy
	}
}`;

export const GET_CHANNELS_PUBLISHERS_DASHBOARD = gql`
	query allChannels {
		allChannels {
			ownerType
			${CHANNEL_FIELDS_FRAGMENT}
			adminUsers {
				_id
			}
			channelActiveUsersCount
			articleCount
			archivedArticleCount
			createdAt
			createdBy
			state
		}
	}`;

export const ADD_PREMIUM_CHANNEL = gql`
	mutation addChannel($item: ChannelInputAdd) {
		addChannel(item: $item) {
			${CHANNEL_FIELDS_FRAGMENT}
			ownerId
			adminUsers {
				_id
				name
			}
		}
	}
`;

export const EDIT_PREMIUM_CHANNEL = gql`
	mutation editChannel($item: ChannelInputEdit) {
		editChannel(item: $item) {
			${CHANNEL_FIELDS_FRAGMENT}
			ownerId
			adminUsers {
				_id
				name
			}
		}
	}
`;

export const ARCHIVE_PREMIUM_CHANNEL = gql`
	mutation archiveChannel($id: ID!){
		archiveChannel(id: $id) {
			_id
		}
	}
`;

export const GET_PREMIUM_CHANNEL_BY_ID = gql`
	query findChannelById($id: ID!) {
		channel: findChannelById(id: $id) {
			${CHANNEL_FIELDS_FRAGMENT}
			userChannelSubscription {
				cancelAtPeriodEnd
				cancelAt
				cancelRequestedAt
				subscribed
				subscriptionState
				subscriptionType
			}
		}
	}
`;

export const GET_CHANNEL_AND_SUBSCRIPTION = gql`
	query findChannelById($id: ID!) {
		channel: findChannelById(id: $id) {
			${CHANNEL_FIELDS_FRAGMENT}
			userChannelSubscription {
				cancelAtPeriodEnd
				cancelAt
				cancelRequestedAt
				subscribed
				subscriptionState
				subscriptionType
				acknowledgedNonChargeSubExpired
			}
		}
	}
`;

export const GET_CHANNEL_WITH_CATEGORIES = gql`
	query findChannelById($id: ID!) {
		channel: findChannelById(id: $id) {
			state
			${CHANNEL_FIELDS_FRAGMENT}
			categories{
				_id
				name
			}
			userChannelSubscription {
				cancelAtPeriodEnd
				cancelAt
				cancelRequestedAt
				subscribed
				subscriptionType
				subscriptionState
				acknowledgedNonChargeSubExpired
			}
		}
	}
`;

interface ChannelFragmentResponse {
	_id: string;
	name: string;
	ownerType: ChannelOwner;
	ownerId: string;
	owner: IdAndName;
	price: number;
	description: string;
	url: string;
	profileImage: string;
	coverImage: string;
	isCurrentUserSubscribed: boolean;
}

export interface ChannelSubscribedUser {
	_id: string;
	name: string;
	email: string;
	channelSubscriptions: ChannelSubscription[];
}

export interface ChannelSubscriberAddEditFragment extends ChannelFragmentResponse {
	ownerId: string;
	glAccount: string;
	adminUsers: IdAndName[];
	defaultTrialDurationDays: number;
	channelSubscribedUsers: ChannelSubscribedUser[];
	type: ChannelTypes;
}

export interface ChannelSubscriberAddEditResponse {
	channel: ChannelSubscriberAddEditFragment;
}

export const GET_PREMIUM_CHANNEL_BY_ID_PUBLISHERS_DASHBOARD = (params: FindChannelByIdParams) => gql`
	query findChannelById($id: ID!) {
		channel: findChannelById(id: $id) {
			${CHANNEL_FIELDS_FRAGMENT}
			ownerId
			glAccount
			articleCount
			templateCount
			channelActiveUsersCount
			state
			adminUsers {
				_id
				name
			}
			channelSubscribedUsers(includePreviouslySubscribedUsers: ${params.includePreviouslySubscribedUsers}) {
				_id
				email
				name
				channelSubscriptions(channelId: $id) {
					subscriptionType
					cancelAt
					notes
					pricePerMonth
					subscriptionState
					isFullArticleContent
					accountNumber
					rebate
					billingMethod
					trialDuration
				}
			}
		}
	}
`;

export const GET_PREMIUM_CHANNEL_SUBSCRIBED_USERS_BY_ID = gql`
	query findChannelById($id: ID!) {
		channel: findChannelById(id: $id) {
			_id
			name
			type
			channelActiveUsersCount
			articleCount
			adminUsers {
				_id
			}
			defaultTrialDurationDays
			type
			state
			channelSubscribedUsers(includePreviouslySubscribedUsers: true) {
				_id
				name
				company
				email
				channelSubscriptions(channelId: $id) {
					channelId
					createdAt
					updatedAt
					subscriptionType
					subscriptionState
					emailNotifications
					rebate
					notes
					cancelAt
					cancelRequestedAt
					cancelAtPeriodEnd
					isFullArticleContent
				}
			}
		}
	}
`;

export const NOTIFY_REGISTERED_CHANNEL_INTEREST = gql`
	mutation notifyRegisteredChannelInterest($item: NotifyRegisterChannelInterestInput!) {
		notifyRegisteredChannelInterest(item: $item) {
			channelName
			userEmail
		}
	}
`;


export const PROCESS_TRIAL_REQUEST = gql`
	mutation processTrialRequest($item: ProcessTrialRequestInput!) {
		processTrialRequest(item: $item){
			_id
		}
	}
`;

export const PROCESS_FREE_CLOSED_REQUEST = gql`
	mutation processFreeClosedRequest($item: ProcessTrialRequestInput!) {
		processFreeClosedRequest(item: $item){
			_id
			name
		}
	}
`;

export interface ProcessTrialRequestInput {
	userId: string;
	channelId: string;
	approved: boolean;
}

export interface FindChannelByIdParams {
	includePreviouslySubscribedUsers: boolean;
}
